import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { resolveLogoPx, FOOTER_LOGO_PX } from "@/lib/heroPresets";
import { Phone, Mail, MapPin, Facebook } from "lucide-react";

const telHref = (phone) => `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;

const FOOTER_LINKS = [
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Services", href: "/#services" },
  { label: "For Families", href: "/for-families" },
  { label: "For Professionals", href: "/for-professionals" },
  { label: "Trilogy Senior Wellness", href: "/trilogy-senior-wellness" },
  { label: "Free Consultation", href: "/book" },
  { label: "Contact", href: "/contact" },
];

export const PublicFooter = ({ footer = {}, footerMenu = [], phone }) => {
  const p = footer.phone || phone;
  const links = footerMenu && footerMenu.length ? footerMenu : FOOTER_LINKS;
  const isExternal = (href) => /^(https?:|mailto:|tel:)/i.test(href || "");
  return (
    <footer data-testid="public-footer" className="bg-[#1C4358] text-white mmm-grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex flex-col items-start gap-3" data-testid="footer-logo">
              <Logo size={resolveLogoPx(footer.logo_size, FOOTER_LOGO_PX)} />
              <span className="font-display font-bold text-xl">MovingMyMama</span>
            </div>
            <p className="mt-5 text-white/80 leading-relaxed max-w-sm text-[16px]">
              {footer.tagline ||
                "Helping families make the next right move for your loved one — with patience, honesty, and heart."}
            </p>
            {footer.facebook_url ? (
              <a
                href={footer.facebook_url}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook"
                className="mmm-tap mt-6 inline-flex items-center gap-2 px-4 h-11 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-[15px] font-semibold"
              >
                <Facebook size={18} /> Follow us on Facebook
              </a>
            ) : null}
          </div>

          <div className="md:col-span-3">
            <h4 className="font-display font-semibold text-[15px] uppercase tracking-wide text-white/60">
              Explore
            </h4>
            <ul className="mt-4 space-y-2.5">
              {links.map((l) => (
                <li key={l.href || l.label}>
                  {isExternal(l.href) || l.new_tab ? (
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noreferrer noopener"
                      data-testid={`footer-link-${(l.href || l.label).replace(/[/#]/g, "") || "home"}`}
                      className="text-white/85 hover:text-white text-[16px] transition-colors"
                    >
                      {l.label}
                    </a>
                  ) : (
                    <Link
                      to={l.href}
                      data-testid={`footer-link-${(l.href || l.label).replace(/[/#]/g, "") || "home"}`}
                      className="text-white/85 hover:text-white text-[16px] transition-colors"
                    >
                      {l.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-display font-semibold text-[15px] uppercase tracking-wide text-white/60">
              Talk with a guide
            </h4>
            <ul className="mt-4 space-y-3.5 text-[16px]">
              {p ? (
                <li>
                  <a href={telHref(p)} data-testid="footer-phone" className="flex items-center gap-3 text-white/90 hover:text-white transition-colors">
                    <Phone size={18} className="text-[#DEE029]" /> {p}
                  </a>
                </li>
              ) : null}
              {footer.email ? (
                <li>
                  <a href={`mailto:${footer.email}`} data-testid="footer-email" className="flex items-center gap-3 text-white/90 hover:text-white transition-colors">
                    <Mail size={18} className="text-[#DEE029]" /> {footer.email}
                  </a>
                </li>
              ) : null}
              {footer.address ? (
                <li className="flex items-center gap-3 text-white/90">
                  <MapPin size={18} className="text-[#DEE029]" /> {footer.address}
                </li>
              ) : null}
            </ul>
            <Link
              to="/book"
              data-testid="footer-book-btn"
              className="mmm-tap mt-6 inline-flex items-center px-5 h-12 rounded-full text-[15px] font-semibold text-[#1C4358] bg-[#DEE029] hover:bg-[#c9cb17] transition-colors"
            >
              Book a Free Consultation
            </Link>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/15 flex flex-col sm:flex-row items-center justify-between gap-4 text-[14px] text-white/60">
          <p>© {new Date().getFullYear()} MovingMyMama. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-white transition-colors">Terms of Use</Link>
            <Link to="/privacy/request" data-testid="footer-privacy-request" className="hover:text-white transition-colors">Privacy Requests</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link to="/login" data-testid="footer-login" className="hover:text-white transition-colors">Staff Login</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
